import { err, ok, type AbortLike, type Result } from '@visious/core';
import type { Platform } from '../platform/instance';
import type { ApiProblem } from './videos';

/**
 * One way to read a JSON body, because there is one trap in it and it was answered five different ways.
 *
 * **A `200` with zero bytes is not "that is not JSON".** `JSON.parse('')` throws the same `SyntaxError`
 * a captive portal's HTML does, so an endpoint that only catches the throw reports *"The instance
 * answered with something that is not JSON — usually a proxy or captive portal answering instead of
 * Invidious"* about a response Invidious itself sent, and points the reader at their network. The
 * zero-byte `200` has **seven** known homes in this API (documented captions, `/api/v1/videos/{id}` for
 * `jNQXAC9IVRw`, `/api/v1/channels/{ucid}/notatab`, `/api/v1/channels/{ucid}/thumbnail`, an empty
 * playlist id, a malformed clip id, an owned-playlist read), so "this endpoint is surely exempt" is not
 * a safe assumption anywhere.
 *
 * Before this, three endpoint modules checked for it and four did not — including
 * `api/channels.ts`, whose own comment recorded reaching the case and reported it as the proxy sentence
 * anyway. The check is one line; having it in one place is what makes it true of the next endpoint too.
 *
 * **For a read only.** `authWrite` makes the opposite reading of the same bytes and is correct to: four
 * of the five playlist mutations answer `204` with an empty body, so there "nothing" is the success. A
 * read that says nothing has failed; a write that says nothing has not.
 */
export function readJsonBody(body: string): Result<unknown, ApiProblem> {
  if (body.trim() === '') return err({ kind: 'empty-response' });
  try {
    return ok(JSON.parse(body) as unknown);
  } catch {
    return err({ kind: 'malformed-json' });
  }
}

/**
 * The whole unauthenticated read: send it through the transport (§3's retry, backoff and per-origin
 * retry), then {@link readJsonBody}. The URL is built by the caller because that is where the
 * `bad-url` arm has a path to name.
 */
export async function fetchJson(
  platform: Platform,
  url: string,
  // `AbortLike`, not `AbortSignal`: screen models call down to here and must stay DOM-free (R13).
  signal?: AbortLike,
): Promise<Result<unknown, ApiProblem>> {
  const response = await platform.transport.send({ method: 'GET', url, headers: {}, signal });
  if (!response.ok) return err({ kind: 'transport', error: response.error });
  return readJsonBody(response.value.body);
}
